import { receiptListQuerySchema, type Page, type ReceiptListQuery } from '@chukua/common';
import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { toListParams } from '../../../../../shared/policies/list-params';
import { useListQuery } from '../../../../../shared/hooks/use-list-query';
import { listReceipts, type ReceiptView } from '../receipts.api';

/** `queryKey` records which query the loaded page answers, so a newer query shows as refreshing over the old rows. */
export type ReceiptsState =
  { status: 'loading' } | { status: 'loaded'; page: Page<ReceiptView>; queryKey: string } | { status: 'forbidden' } | { status: 'error' };

const keyOf = (query: ReceiptListQuery) => toListParams(query).toString();

function toState(page: Page<ReceiptView>, query: ReceiptListQuery): ReceiptsState {
  return { status: 'loaded', page, queryKey: keyOf(query) };
}

function toErrorState(err: unknown): ReceiptsState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/** The page of receipts the URL's list query asks for (`useListQuery`), re-fetched whenever that query changes. */
export function useReceiptList() {
  const list = useListQuery(receiptListQuerySchema);
  const { query } = list;
  const [state, setState] = useState<ReceiptsState>({ status: 'loading' });
  // The query whose refresh failed: the warning belongs to that page, so it goes away when the query changes.
  const [reloadFailedFor, setReloadFailedFor] = useState<string | null>(null);
  // Only the latest request may write state: a slow earlier response must not overwrite a newer one.
  const latestRequest = useRef(0);

  useEffect(() => {
    const request = ++latestRequest.current;

    listReceipts(query)
      .then((page) => {
        if (request === latestRequest.current) setState(toState(page, query));
      })
      .catch((err) => {
        if (request === latestRequest.current) setState(toErrorState(err));
      });
  }, [query]);

  const isRefreshing = state.status === 'loaded' && state.queryKey !== keyOf(query);

  /** Re-fetches the current page after a receipt is recorded, so the new row shows without a reload. The receipt is already saved, so a failure keeps the rows on screen. */
  async function reload(): Promise<void> {
    const request = ++latestRequest.current;
    try {
      const page = await listReceipts(query);
      if (request !== latestRequest.current) return;
      setReloadFailedFor(null);
      setState(toState(page, query));
    } catch {
      if (request === latestRequest.current) setReloadFailedFor(keyOf(query));
    }
  }

  const hasReloadFailed = reloadFailedFor === keyOf(query);

  return { state, list, isRefreshing, hasReloadFailed, reload };
}
